import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  Eye,
  MousePointerClick,
  Search,
  ExternalLink,
  KeyRound,
  Info,
} from "lucide-react";
import api, { fmtErr } from "../api";

/**
 * Admin · SEO Dashboard — /admin/seo
 *
 * Pulls per-supplier-slug clicks/impressions/positions from Bing Webmaster
 * Tools (GetPageStats). Refresh on demand; the API returns weekly buckets,
 * so daily refresh is sufficient.
 *
 * Empty state when BING_WEBMASTER_API_KEY isn't configured yet — gives the
 * exact setup steps inline.
 */
export default function AdminSeo() {
  const [overview, setOverview] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [refreshes, setRefreshes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [refreshError, setRefreshError] = useState("");
  const [sort, setSort] = useState("impressions");
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [o, s, h] = await Promise.all([
        api.get("/admin/seo/overview"),
        api.get("/admin/seo/suppliers", { params: { sort } }),
        api.get("/admin/seo/refreshes"),
      ]);
      setOverview(o.data);
      setSuppliers(s.data.items || []);
      setRefreshes(h.data.items || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    (async () => {
      await load();
    })();
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/seo/suppliers", { params: { sort } });
        setSuppliers(data.items || []);
      } catch (_) {
        /* ignore — overview load surfaces errors */
      }
    })();
  }, [sort]);

  async function handleRefresh() {
    setRefreshing(true);
    setRefreshResult(null);
    setRefreshError("");
    try {
      const { data } = await api.post("/admin/seo/refresh");
      setRefreshResult(data);
      await load();
    } catch (err) {
      setRefreshError(fmtErr(err.response?.data?.detail || err.message));
    } finally {
      setRefreshing(false);
    }
  }

  const filtered = useMemo(() => {
    if (!q.trim()) return suppliers;
    const qq = q.toLowerCase();
    return suppliers.filter(
      (s) =>
        (s.company_name || "").toLowerCase().includes(qq) ||
        (s.slug || "").toLowerCase().includes(qq) ||
        (s.industry || "").toLowerCase().includes(qq) ||
        (s.immex_number || "").toLowerCase().includes(qq)
    );
  }, [suppliers, q]);

  const keyConfigured = !!overview?.api_key_configured;
  const totals = overview?.totals || {};

  return (
    <div className="container-cd py-12" data-testid="admin-seo-page">
      <div className="overline">ADMIN · SEO DASHBOARD</div>
      <div className="flex flex-wrap items-end justify-between gap-3 mt-2">
        <div>
          <h1 className="font-display text-4xl md:text-5xl leading-[0.95]">
            Search Visibility
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }}>
            Bing Webmaster · per-supplier crawl, impression & click stats
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/suppliers"
            className="btn btn-ghost btn-sm inline-flex items-center gap-1"
            data-testid="admin-seo-suppliers-link"
          >
            Suppliers
          </Link>
          <button
            onClick={handleRefresh}
            disabled={refreshing || !keyConfigured}
            className="btn btn-primary btn-sm inline-flex items-center gap-1"
            data-testid="admin-seo-refresh-btn"
            title={
              keyConfigured
                ? "Pull latest data from Bing Webmaster Tools"
                : "Configure BING_WEBMASTER_API_KEY first"
            }
          >
            {refreshing ? (
              <>
                <Loader2 size={12} className="animate-spin" /> Refreshing…
              </>
            ) : (
              <>
                <RefreshCw size={12} /> Refresh from Bing
              </>
            )}
          </button>
        </div>
      </div>

      {/* Refresh result toast */}
      {refreshResult ? (
        <div
          className="mt-4 cd-card p-4 font-mono text-xs"
          style={{ borderColor: "var(--green)", background: "rgba(0,160,90,0.04)" }}
          data-testid="admin-seo-refresh-result"
        >
          <CheckCircle2 size={12} className="inline -mt-0.5" style={{ color: "var(--green)" }} />{" "}
          Refreshed · {refreshResult.rows_upserted} rows upserted ·{" "}
          {refreshResult.supplier_paths_touched} supplier paths touched
        </div>
      ) : null}
      {refreshError ? (
        <div
          className="mt-4 cd-card p-4 font-mono text-xs"
          style={{ borderColor: "var(--red)", background: "rgba(220,0,0,0.04)" }}
          data-testid="admin-seo-refresh-error"
        >
          <AlertCircle size={12} className="inline -mt-0.5" style={{ color: "var(--red)" }} />{" "}
          {refreshError}
        </div>
      ) : null}

      {/* Setup CTA when no API key configured */}
      {!loading && !keyConfigured ? (
        <SetupGuide />
      ) : null}

      {/* Top-line metrics */}
      <section
        className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3"
        data-testid="admin-seo-totals"
      >
        <Metric
          icon={<Eye size={14} />}
          label="IMPRESSIONS"
          value={fmtInt(totals.impressions)}
          testid="metric-impressions"
        />
        <Metric
          icon={<MousePointerClick size={14} />}
          label="CLICKS"
          value={fmtInt(totals.clicks)}
          testid="metric-clicks"
        />
        <Metric
          icon={<TrendingUp size={14} />}
          label="CTR"
          value={`${totals.ctr_percent ?? 0}%`}
          testid="metric-ctr"
        />
        <Metric
          icon={<Search size={14} />}
          label="INDEXED SLUGS"
          value={`${totals.indexed_supplier_slugs ?? 0} / ${totals.total_supplier_slugs ?? 0}`}
          testid="metric-indexed"
        />
      </section>

      {/* Weekly bucket notice */}
      <div
        className="mt-4 flex items-start gap-2 text-xs"
        style={{ color: "var(--ink-2)" }}
        data-testid="admin-seo-bucket-notice"
      >
        <Info size={12} style={{ marginTop: 2 }} />
        <span>
          Bing aggregates data in <strong>weekly buckets</strong>. Recent data points may take 48–72 hours to appear. Refresh once per day; running it more often won&apos;t yield fresher numbers.
        </span>
      </div>

      {/* Per-supplier table */}
      <section className="mt-10" data-testid="admin-seo-table-section">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="overline">PER-SUPPLIER · {filtered.length} ROWS</div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="cd-input font-mono text-xs"
              style={{ maxWidth: 220, height: 36 }}
              data-testid="admin-seo-sort"
            >
              <option value="impressions">Sort: Impressions ↓</option>
              <option value="clicks">Sort: Clicks ↓</option>
              <option value="ctr">Sort: CTR ↓</option>
              <option value="position">Sort: Position ↑ (best)</option>
              <option value="name">Sort: Name A→Z</option>
            </select>
            <input
              type="text"
              placeholder="Filter by name / IMMEX / city…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="cd-input font-mono text-xs"
              style={{ maxWidth: 280, height: 36 }}
              data-testid="admin-seo-filter"
            />
          </div>
        </div>

        {loading ? (
          <div className="mt-6 overline">LOADING…</div>
        ) : (
          <div className="mt-3 cd-card p-0 overflow-x-auto">
            <table className="cd-table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>City</th>
                  <th>Status</th>
                  <th className="text-right">Impressions</th>
                  <th className="text-right">Clicks</th>
                  <th className="text-right">CTR</th>
                  <th className="text-right">Avg position</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.slug} data-testid={`admin-seo-row-${s.slug}`}>
                    <td>
                      <Link
                        to={`/suppliers/${s.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold hover:underline"
                      >
                        {s.company_name}
                      </Link>
                      <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                        {s.industry || "—"} · IMMEX {s.immex_number || "—"}
                      </div>
                    </td>
                    <td className="font-mono text-xs">{s.city || "—"}</td>
                    <td>
                      <StatusChip status={s.profile_status} />
                    </td>
                    <td className="font-mono text-xs text-right">{fmtInt(s.impressions)}</td>
                    <td className="font-mono text-xs text-right">{fmtInt(s.clicks)}</td>
                    <td
                      className="font-mono text-xs text-right"
                      style={{ color: s.ctr_percent >= 3 ? "var(--green)" : undefined }}
                    >
                      {s.ctr_percent}%
                    </td>
                    <td className="font-mono text-xs text-right">
                      {s.avg_impression_position ? s.avg_impression_position.toFixed(1) : "—"}
                    </td>
                    <td className="text-right">
                      <Link
                        to={`/suppliers/${s.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-ghost btn-sm inline-flex items-center gap-1"
                        data-testid={`admin-seo-open-${s.slug}`}
                      >
                        Open <ExternalLink size={10} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 ? (
              <div
                className="p-8 text-center font-mono text-xs"
                style={{ color: "var(--ink-2)" }}
              >
                {keyConfigured
                  ? "No data yet. Click 'Refresh from Bing' to pull the latest stats."
                  : "Configure your Bing Webmaster API key (see setup guide above) to start pulling data."}
              </div>
            ) : null}
          </div>
        )}
      </section>

      {/* Refresh history */}
      <section className="mt-10" data-testid="admin-seo-history-section">
        <div className="overline">REFRESH HISTORY</div>
        {refreshes.length === 0 ? (
          <p className="mt-3 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
            No refreshes yet.
          </p>
        ) : (
          <div className="mt-3 cd-card p-0 overflow-x-auto">
            <table className="cd-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>By</th>
                  <th className="text-right">Rows in payload</th>
                  <th className="text-right">Rows upserted</th>
                  <th className="text-right">Supplier paths</th>
                </tr>
              </thead>
              <tbody>
                {refreshes.map((r) => (
                  <tr key={r.refresh_id} data-testid={`admin-seo-refresh-${r.refresh_id}`}>
                    <td className="font-mono text-xs">{r.created_at}</td>
                    <td className="font-mono text-xs">{r.performed_by || "—"}</td>
                    <td className="font-mono text-xs text-right">{fmtInt(r.rows_in_payload)}</td>
                    <td className="font-mono text-xs text-right">{fmtInt(r.rows_upserted)}</td>
                    <td className="font-mono text-xs text-right">{fmtInt(r.supplier_paths_touched)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function fmtInt(n) {
  return typeof n === "number" ? n.toLocaleString("en-US") : "0";
}

function Metric({ icon, label, value, testid }) {
  return (
    <div className="cd-card p-4" data-testid={testid}>
      <div
        className="overline inline-flex items-center gap-1.5"
        style={{ color: "var(--ink-2)" }}
      >
        {icon} {label}
      </div>
      <div className="font-display text-3xl mt-2 leading-none">{value}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    verified_founding_member: { bg: "var(--orange)", color: "white", label: "FOUNDING" },
    claimed: { bg: "var(--green)", color: "white", label: "CLAIMED" },
    claim_pending: { bg: "#ea580c", color: "white", label: "PENDING" },
    unclaimed: {
      bg: "var(--bg)",
      color: "var(--ink-2)",
      label: "UNCLAIMED",
      border: "1px solid var(--line)",
    },
  };
  const cfg = map[status] || map.unclaimed;
  return (
    <span
      className="cd-chip"
      style={{
        background: cfg.bg,
        color: cfg.color,
        border: cfg.border,
        fontSize: 10,
      }}
    >
      {cfg.label}
    </span>
  );
}

function SetupGuide() {
  return (
    <section
      className="mt-6 cd-card p-6"
      style={{
        background: "rgba(255,107,0,0.04)",
        borderColor: "var(--orange)",
      }}
      data-testid="admin-seo-setup-guide"
    >
      <div className="overline inline-flex items-center gap-2" style={{ color: "var(--orange)" }}>
        <KeyRound size={12} /> ONE-TIME SETUP — BING WEBMASTER TOOLS
      </div>
      <h3 className="font-heading text-2xl mt-3">
        Configure your Bing API key to start pulling per-supplier stats
      </h3>
      <ol
        className="mt-4 space-y-3 text-sm"
        style={{ listStyle: "decimal", paddingLeft: 20 }}
      >
        <li>
          Add <code>corridordigital.net</code> to your{" "}
          <a
            href="https://www.bing.com/webmasters/"
            target="_blank"
            rel="noreferrer"
            className="hover:underline"
            style={{ color: "var(--orange)" }}
          >
            Bing Webmaster Tools dashboard
          </a>{" "}
          and complete site verification (HTML meta tag, XML file, or DNS).
        </li>
        <li>
          Open <strong>Settings → API Access</strong> and click <strong>Generate</strong>. Copy the
          alphanumeric key.
        </li>
        <li>
          Paste it into <code>/app/backend/.env</code> as <code>BING_WEBMASTER_API_KEY=&quot;...&quot;</code> and restart the backend{" "}
          <span style={{ color: "var(--ink-2)" }}>
            (or send it to your dev agent and they&apos;ll set it).
          </span>
        </li>
        <li>
          Refresh this page → the <strong>Refresh from Bing</strong> button will become active. Click
          it; rows will populate within ~10 seconds.
        </li>
      </ol>
      <p
        className="mt-5 text-xs font-mono"
        style={{ color: "var(--ink-2)" }}
      >
        Bing aggregates data weekly — first useful data shows up 7–10 days after site verification.
      </p>
    </section>
  );
}
